import React from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import { useLanguage } from '../contexts/LanguageContext';
import { HERO_VEHICLE } from '../data/photos';
import { Button } from './Button';
import { HeroHook } from './HeroHook';
import { trackHeroCTA } from '../tracking';
import { scrollToModels } from '../utils/scroll';
export function Hero() {
  const {
    t
  } = useLanguage();
  const reduce = useReducedMotion();
  const chips = ['hero.chip1', 'hero.chip2', 'hero.chip3', 'hero.chip4'];
  const rise = (delay: number) => reduce ? {} : {
    initial: {
      opacity: 0,
      y: 18,
      rotateX: 8
    },
    animate: {
      opacity: 1,
      y: 0,
      rotateX: 0
    },
    transition: {
      duration: 0.7,
      delay,
      ease: [0.22, 1, 0.36, 1] as const
    },
    style: {
      transformOrigin: 'center top' as const
    }
  };

  // The headline's final word carries the gold underline — the line itself stays Midnight.
  const headlineWords = t('hero.headline2').split(' ');
  const lastWord = headlineWords.pop() ?? '';
  const leadWords = headlineWords.join(' ');
  return <section id="top" className="w-full bg-white" data-id="element-130" data-mp="3o7lshs6gf8ph">
      <div className="mx-auto grid w-full max-w-7xl grid-cols-1 items-center gap-12 px-4 pb-14 pt-10 sm:px-6 sm:pb-20 sm:pt-16 lg:grid-cols-[1.08fr_1fr] lg:gap-10" style={{
      perspective: 1400
    }} data-id="element-131" data-mp="3o6aq9wsnq90h">
        <div data-id="element-132" data-mp="3e7l6fyh7aehl">
          <motion.p {...rise(0)} className="font-display text-[10px] font-bold uppercase leading-relaxed tracking-[0.26em] text-camelink sm:text-[11px]" data-id="element-133" data-mp="3ebioegs1kzzw">
            {t('hero.eyebrow')}
          </motion.p>
          <motion.div {...rise(0.05)} aria-hidden="true" className="mt-3 h-[3px] w-12 bg-kred" data-id="element-134" data-mp="2qkmsp8kgtknd" />

          <motion.h1 {...rise(0.1)} className="mt-6 font-display text-[2.6rem] font-extrabold uppercase leading-[0.9] tracking-[-0.035em] text-ink [text-wrap:balance] sm:text-[4.5rem] lg:text-[5.25rem]" data-id="element-135" data-mp="2vi53z5lygbyj">
            {t('hero.headline1')}
            <br data-id="element-136" data-mp="s21iabtr0dzz" />
            {leadWords ? `${leadWords} ` : ''}
            <span className="relative inline-block" data-id="element-137" data-mp="s3ck463mvbog">
              <span aria-hidden="true" className="absolute inset-x-0 bottom-[0.06em] h-[5px] bg-gold sm:h-[8px]" data-id="element-138" data-mp="nat7bbcrskkp" />
              <span className="relative" data-id="element-139" data-mp="ia5f4mct1xow">{lastWord}</span>
            </span>
          </motion.h1>

          <motion.div {...rise(0.18)} className="mt-7" data-id="element-140" data-mp="db2f5gbomhns">
            <HeroHook data-id="element-141" data-mp="1azulzgc74ryf" />
          </motion.div>

          <motion.p {...rise(0.24)} className="mt-6 max-w-[46ch] text-[1.0625rem] leading-[1.7] text-body [text-wrap:pretty] sm:text-lg" data-id="element-142" data-mp="1ayjg9az6xa4o">
            {t('hero.sub')}
          </motion.p>

          {/* One CTA only. A WhatsApp button this early pulled visitors into a chat
              before they had read anything — and before the form could capture the
              lead, consent, or a conversion event. WhatsApp lives further down. */}
          <motion.div {...rise(0.3)} className="mt-9 flex w-full flex-col gap-3 sm:max-w-md sm:flex-row" data-id="element-143" data-mp="3hq248rtd3w77">
            <Button type="button" onClick={() => {
            trackHeroCTA();
            scrollToModels();
          }} className="sm:flex-1" data-id="element-144" data-mp="21amvn8ncdqlt">
              {t('hero.ctaPrimary')}
            </Button>
          </motion.div>

          {/* Plain text separated by a camel dot — no boxes, nothing that looks tappable. */}
          <motion.p {...rise(0.4)} className="mt-8 text-[0.9375rem] leading-relaxed tracking-[0.01em] text-muted" data-id="element-145" data-mp="2kyvolkwpm0md">
            {chips.map((chip, index) => <React.Fragment key={chip} data-id="element-146" data-mp="2oynwrwi6y4r7" data-mpi={index}>
                {index > 0 && <span aria-hidden="true" className="mx-2 text-camel" data-id="element-147" data-mp="2tysfzuahhdl4">
                    ·
                  </span>}
                {t(chip)}
              </React.Fragment>)}
          </motion.p>

          <motion.p {...rise(0.5)} className="mt-10 font-display text-[11px] font-bold uppercase tracking-[0.3em] text-ink/70" data-id="element-148" data-mp="4bdwfeqaa7o3">
            {t('hero.tagline')}
          </motion.p>
        </div>

        {/* Desktop-only support image. It fills the right column so the headline is not
            left floating against empty white. No overlay, scrim, or filter. */}
        <motion.div {...rise(0.25)} aria-hidden="true" className="hidden lg:block" data-id="element-149" data-mp="3hqaa6rucsl2k">
          <img src={HERO_VEHICLE} alt="" loading="eager" decoding="async" className="h-auto w-full object-contain" data-id="element-150" data-mp="3cryz6dsbo4ir" />
        </motion.div>
      </div>
    </section>;
}